import { makeAutoObservable, runInAction } from 'mobx';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import AuthApiService from '../services/api/AuthApiService';
import languageStore from './languageStore';
import { AUTH_TOKENS_KEY, clearAuthToken, getAuthToken, setAuthToken, } from '../services/api/tokenStorage';

export { AUTH_TOKENS_KEY };
type AppJwtPayload = JwtPayload & Record<string, unknown>;

class AuthStore {
    public isLoading = false;
    public currentUser: AppJwtPayload | null = null;
    public error: string | null = null;
    public registrationPartiallyCompleted = false;
    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }
    public checkIsAuthorized() {
        const token = getAuthToken();
        try {
            if (!token) {
                this.currentUser = null;
                return false;
            }
            const decoded = jwtDecode<AppJwtPayload>(token);
            if (decoded.exp && decoded.exp * 1000 <= Date.now()) {
                this.clearUserInfo();
                return false;
            }
            this.currentUser = decoded;
        }
        catch (e) {
            console.warn('[AuthStore.checkIsAuthorized] decode error:', e);
            this.currentUser = null;
        }
        return this.currentUser !== null;
    }
    public async signIn(username: string, password: string, rememberMe = false) {
        try {
            this.isLoading = true;
            this.error = null;
            const data = await AuthApiService.login({
                username,
                password,
            });
            if (!data?.token) {
                throw new Error('Token not returned from server');
            }
            setAuthToken(data.token, rememberMe);
            if (data.language) {
                languageStore.setLanguageFromBackend(data.language);
            }
            const decoded = jwtDecode<AppJwtPayload>(data.token);
            if (decoded.exp && decoded.exp * 1000 <= Date.now()) {
                throw new Error('Server returned an expired token');
            }
            runInAction(() => {
                this.currentUser = decoded;
                this.error = null;
            });
        }
        catch (e: any) {
            clearAuthToken();
            runInAction(() => {
                this.currentUser = null;
                this.error = e?.response?.data?.error || e?.message || 'Login failed';
            });
        }
        finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }
    public async register(userData: {
        username: string;
        password: string;
        email?: string;
        birthDate?: string;
        gender?: string;
        height?: number;
        weight?: number;
        dominantHand?: string;
        activityLevel?: string;
        healthLimitations?: string[];
        language?: string;
    }) {
        try {
            this.isLoading = true;
            this.error = null;
            this.registrationPartiallyCompleted = false;
            const payload = {
                username: userData.username,
                password: userData.password,
                email: userData.email,
                birth_date: userData.birthDate,
                gender: userData.gender,
                height: userData.height,
                weight: userData.weight,
                handedness: userData.dominantHand,
                activity_level: userData.activityLevel,
                limitations: userData.healthLimitations,
                role: 'user',
                language: userData.language || languageStore.language,
            };
            await AuthApiService.register(payload);
            if (userData.email) {
                try {
                    await AuthApiService.registerSecondary({
                        username: userData.username,
                        password: userData.password,
                        email: userData.email,
                    });
                } catch (secondaryError: any) {
                    runInAction(() => {
                        this.registrationPartiallyCompleted = true;
                    });
                    const isEnglish = languageStore.language === 'en';
                    throw new Error(
                        secondaryError?.status === 409
                            ? (isEnglish
                                ? 'The primary account was created, but the game account already exists. Sign in or contact support.'
                                : 'Основная учётная запись создана, но игровая учётная запись уже существует. Войдите или обратитесь в поддержку.')
                            : (isEnglish
                                ? 'The primary account was created, but game registration was not completed. Contact support to synchronize the account.'
                                : 'Основная учётная запись создана, но игровая регистрация не завершена. Обратитесь в поддержку, чтобы синхронизировать аккаунт.'),
                    );
                }
            }
            runInAction(() => { this.error = null; });
        }
        catch (e: any) {
            runInAction(() => {
                this.error = e?.response?.data?.error || e?.message || 'Registration failed';
            });
            console.error(e);
        }
        finally {
            runInAction(() => { this.isLoading = false; });
        }
    }
    public clearUserInfo() {
        clearAuthToken();
        this.currentUser = null;
        this.error = null;
        this.registrationPartiallyCompleted = false;
    }
}
export default new AuthStore();
